import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GraphQL
import {
  selectOrderWithActionBasicInfoById,
  selectLocationValues,
  selectEditionValues,
  selectOperatorsAndVehiclesFormValues,
} from '../../../../../gql/selectors/actions';

// Utils
import {
  compressSessionStorage,
  decompressSessionStorage,
} from '../../../../../utils/sessionStorage';

// Constants
import {
  formActionNaming,
  formActionValidationNaming,
} from '../../../../../components/forms/ActionForms/constants';

// Forms
import { buildOperatorsFormFromServer } from '../OperatorsAndVehicles/formFromServer';

// GQL
import { client } from '../../../../../gql/client';
import { GET_ACTION_BY_ID } from '../../../../../gql/queries/actions/actions';
import { reconcileValidationActionContext } from '../../../../../utils/validationActionContext';
import {
  claimDraftScope,
  isDraftOwner,
} from '../../../../../utils/draftOwnership';
import {
  ACTION_MEDIA_SCOPE,
  ACTION_VALIDATION_SCOPE,
} from '../../../../../utils/draftScopes';

const loadActionDetails = async (actionId: string) => {
  try {
    const {
      data: { action },
    } = await client.query({
      query: GET_ACTION_BY_ID,
      variables: {
        id: actionId,
      },
    });
    return await selectOrderWithActionBasicInfoById(
      action.orderIDs[0],
      actionId
    );
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Action order loading error:', err);
    return false;
  }
};

/**
 * ✅ FIX: Pre-cargar datos de Location y Edition del servidor
 *
 * PROBLEMA: Cuando el usuario no visita las pestañas de Location/Edition,
 * las keys de sessionStorage no existen y saveData usa valores vacíos/null.
 *
 * SOLUCIÓN: Pre-cargar los datos del servidor en sessionStorage al iniciar
 * el flujo de validación. Incluimos `dataFromServer` para que `difference()`
 * pueda detectar si hubo cambios reales.
 */
const preloadLocationAndEditionData = async (actionId: string) => {
  // Pre-cargar Location si no existe en sessionStorage
  const locationFormKey =
    formActionValidationNaming.NEW_VALIDATION_LOCATION_FORM;
  if (!sessionStorage.getItem(locationFormKey)) {
    try {
      const locationData = await selectLocationValues(actionId);
      // Mientras se leía se pudo abrir otra actuación: sus copias no son
      // estas (spec 2026-11-02, carrera de la precarga).
      if (!isDraftOwner(ACTION_VALIDATION_SCOPE, actionId)) return;
      sessionStorage.setItem(
        locationFormKey,
        compressSessionStorage(
          JSON.stringify({
            ...locationData,
            dataFromServer: locationData, // Para comparación en difference()
          })
        )
      );
      console.log(
        '✅ [ActionPageLoader] Pre-cargados datos de Location del servidor'
      );
    } catch (err) {
      Sentry.captureException(err);
      console.error('⚠️ [ActionPageLoader] Error pre-cargando Location:', err);
    }
  }

  // Pre-cargar Edition si no existe en sessionStorage
  const editionFormKey =
    formActionValidationNaming.NEW_VALIDATION_EDITION_FORM;
  if (!sessionStorage.getItem(editionFormKey)) {
    try {
      const editionData = await selectEditionValues(actionId);
      if (!isDraftOwner(ACTION_VALIDATION_SCOPE, actionId)) return;
      sessionStorage.setItem(
        editionFormKey,
        compressSessionStorage(
          JSON.stringify({
            ...editionData,
            dataFromServer: editionData, // Para comparación en difference()
          })
        )
      );
      console.log(
        '✅ [ActionPageLoader] Pre-cargados datos de Edition del servidor'
      );
    } catch (err) {
      Sentry.captureException(err);
      console.error('⚠️ [ActionPageLoader] Error pre-cargando Edition:', err);
    }
  }
};

/**
 * Pre-carga Operarios igual que Location/Edition (S2, 2026-10-07).
 *
 * Sin esto, saltar de pestaña sin «Continuar» en Operarios —o antes de que la
 * pestaña termine de cargar, que en la tablet tarda— dejaba su clave vacía: el
 * envío reventaba en silencio y, si se llegaba a guardar, el relleno vacío de
 * `saveData` borraba vehículos y empresas externas.
 *
 * Solo rellena lo que FALTA, y lo comprueba otra vez tras el `await`: si
 * mientras tanto el formulario guardó lo que el usuario tocó, eso manda.
 *
 * Spec: docs/superpowers/specs/2026-10-07-wizard-tab-jump-design.md
 */
const preloadOperatorsData = async (actionId: string) => {
  const operatorsFormKey =
    formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM;
  if (sessionStorage.getItem(operatorsFormKey)) return;
  try {
    const operatorsData = await selectOperatorsAndVehiclesFormValues(actionId);
    if (sessionStorage.getItem(operatorsFormKey)) return;
    if (!isDraftOwner(ACTION_VALIDATION_SCOPE, actionId)) return;
    sessionStorage.setItem(
      operatorsFormKey,
      compressSessionStorage(
        JSON.stringify(buildOperatorsFormFromServer(operatorsData))
      )
    );
    console.log(
      '✅ [ActionPageLoader] Pre-cargados datos de Operarios del servidor'
    );
  } catch (err) {
    Sentry.captureException(err);
    console.error('⚠️ [ActionPageLoader] Error pre-cargando Operarios:', err);
  }
};

const preloadFormsFromServer = (actionId: string) =>
  Promise.all([
    preloadLocationAndEditionData(actionId),
    preloadOperatorsData(actionId),
  ]);

export const LoaderActionValidationPageOrder: LoaderFunction = async ({
  params,
}) => {
  // La URL manda: si el contexto de la pestaña es de OTRA actuación se borra
  // entero y esta carga sale por el `:id` de la URL. Síncrono y antes de
  // cualquier `await` (diagnóstico 2026-10-08 § 8).
  reconcileValidationActionContext(params.id);
  // La lista de fotos y el croquis en curso también son de la actuación
  // de la URL. Las carpetas de OPFS no se tocan: las comparten las
  // pestañas (spec 2026-11-02 wizard-draft-ownership § 6).
  claimDraftScope(ACTION_MEDIA_SCOPE, params.id);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  const stringifyForm = sessionStorage.getItem(
    formActionNaming.NEW_VALIDATION_ACTION_DATA
  );

  if (stringifyForm) {
    const sessionStorageData = JSON.parse(
      decompressSessionStorage(stringifyForm)
    );
    const actionId = sessionStorageData.id;

    console.log('[PARTIAL-DEBUG] ActionPage loader — actionId:', actionId);

    // ✅ FIX: Pre-cargar Location, Edition y Operarios del servidor
    await preloadFormsFromServer(actionId);

    const data = await loadActionDetails(actionId);
    console.log('[PARTIAL-DEBUG] ActionPage loader — state:', (data as any)?.state, '| orderId:', (data as any)?.orderId);

    // Log sessionStorage state for ALL tabs
    const tabKeys = Object.entries(formActionValidationNaming);
    console.log('[PARTIAL-DEBUG] SessionStorage state for all tabs:');
    tabKeys.forEach(([name, key]) => {
      const val = sessionStorage.getItem(key);
      console.log(`  ${name}: ${val ? `EXISTS (${val.length} chars)` : 'EMPTY'}`);
    });

    return data;
  }

  // BUG #5 FIX: When navigating via double-click, sessionStorage doesn't have data
  // Use params.id directly to load action details
  if (params.id) {
    // ✅ FIX: Pre-cargar Location, Edition y Operarios del servidor
    await preloadFormsFromServer(params.id);

    const data = await loadActionDetails(params.id);
    return data;
  }

  return {};
};

  return { data: load() };
};
